import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import styles from '../VirtualDatasetViever.styles';
import { format } from 'date-fns/format';
import React, { Suspense, useCallback, useMemo, useState } from 'react';
import useBundleTranslation from 'i18n';
import { DatasetData, DatasetInfo } from 'components/dataset-viewer';
import TopicsBlock from 'app/search/global/results/block/TopicsBlock';
import GlossaryTermPopup from 'components/glossary-term-popup';
import { useElementMetadataSettings } from 'hooks/useElementMetadata';
import { IElementData, IElementDataSettings } from 'components/common/element-data/types';
import ElementDataContainer from 'components/common/element-data/ElementDataContainer';
import ElementDataRow from 'components/common/element-data/ElementDataRow';
import isOwnerVisible from 'tools/owners';
import { UserAuth } from 'store/auth';
import { useAppSelector } from 'store/hooks';
import { getOwnerIconConfig, getSectionElementMetadataSettings } from 'components/common/element-data/functions';
import SummarySection from 'components/common/element-data/sections/SummarySection';
import DetailsSection from 'components/common/element-data/sections/DetailsSection';
import CustomFieldsSection from 'components/common/element-data/sections/CustomFieldsSection';

function DatasetMainInfo({ datasetInfo, datasetData }: { datasetInfo: DatasetInfo; datasetData: DatasetData }) {
    const { t } = useBundleTranslation(['components/dataset-viewer/dataset_viewer', 'components/common/navbar/navbar']);
    const [showTermsPopup, setShowTermsPopup] = useState(false);
    const [topicId, setTopicId] = useState(0);
    const { configuration }: UserAuth = useAppSelector((state) => state.auth);

    const navigate = useNavigate();

    const handleTagClick = useCallback((isTagsOptions: boolean, topic: { topic_id: number | null; name: string }) => {
        if (isTagsOptions) {
            navigate(`/search/global?tag_name=${topic.name}`);
        } else {
            if (topic.topic_id) {
                setShowTermsPopup(true);
                setTopicId(topic.topic_id);
            }
        }
    }, []);

    const topicIds: number[] = useMemo(() => {
        const result: number[] = [];

        if (datasetInfo && datasetInfo.topics && Object.keys(datasetInfo.topics).length > 0) {
            Object.keys(datasetInfo.topics).forEach((topicId: string) => {
                if (topicId === 'default-topic-type') {
                    return;
                }

                datasetInfo?.topics?.[topicId].topics.forEach((topic) => {
                    if (topic.topic_id && !result.includes(topic.topic_id)) {
                        result.push(topic.topic_id);
                    }
                });
            });
        }

        return result;
    }, [datasetInfo]);

    let metadataSettings = useElementMetadataSettings('default');
    const elementData: IElementData = {
        elementType: 'virtual-dataset',
        certifiedInd: datasetInfo.certified === 'Y' && !!datasetInfo.certified_date,
        certificationLevelColor: 'success.main',
        lastCertifiedByName: datasetInfo.certified_by_dn,
        lastCertifiedTime:
            datasetInfo?.certified_date && '0000-00-00 00:00:00' != datasetInfo.certified_date
                ? format(new Date(datasetInfo.certified_date), 'M/dd/yyyy')
                : '',
        categoryPath: datasetInfo.category_name,
        ownerEmailSubject: datasetData.name,
        businessOwner: datasetInfo.business_owner_dn,
        businessOwnerEmail: datasetInfo.business_owner_email,
        technicalOwner: datasetInfo.technical_owner_dn,
        technicalOwnerEmail: datasetInfo.technical_owner_email,
        dataSteward: datasetInfo.data_steward_dn,
        dataStewardEmail: datasetInfo.data_steward_email,
        customFields: datasetInfo.custom_fields,
    };

    ['business_owner', 'technical_owner', 'data_steward'].forEach((ownerField) => {
        if (metadataSettings.fields[ownerField]?.display) {
            metadataSettings.fields[ownerField].display =
                isOwnerVisible((configuration.owners as any)[ownerField], 'elements') &&
                isOwnerVisible((configuration.owners as any)[ownerField], 'tile_preview');
        }
    });

    const elementDataSettings: IElementDataSettings = {
        metadata: metadataSettings,
    };
    const ownerIconConfig = getOwnerIconConfig(elementDataSettings, elementData);
    const { sectionMetadataOrder: topicsBlockOrder } = getSectionElementMetadataSettings(
        elementDataSettings,
        'glossary_terms',
    );

    return (
        <Box sx={styles.box}>
            <Box sx={styles.row}>
                <ElementDataContainer showHeader={false} size={'medium'}>
                    <SummarySection {...elementData} settings={elementDataSettings} />
                    <ElementDataRow order={topicsBlockOrder}>
                        <Box sx={{ width: '100%', '.topics-block': { mt: 0 } }}>
                            <TopicsBlock
                                topics={datasetInfo.topics}
                                handleTagClick={handleTagClick}
                                elementId={datasetData.dataset_id}
                            />
                        </Box>
                    </ElementDataRow>
                    <DetailsSection {...elementData} settings={elementDataSettings} />
                    <CustomFieldsSection {...elementData} settings={elementDataSettings} />
                </ElementDataContainer>
            </Box>
            <Suspense>
                {showTermsPopup && (
                    <GlossaryTermPopup
                        setShowPopup={setShowTermsPopup}
                        showPopup={showTermsPopup}
                        topicIds={topicIds}
                        topicId={topicId}
                    />
                )}
            </Suspense>
        </Box>
    );
}

export default DatasetMainInfo;
